/**
 * Video Forum — Hermes Music
 *
 * Web components del foro de videos (page.gallery-video + detalle de video).
 * Consumen la app custom `hermes-video-forum` vía App Proxy en /apps/video-forum
 * (mismo dominio, rutas relativas, sin CORS).
 *
 * Componentes:
 *   <video-forum-feed>   Filtro por categoría, búsqueda, orden por fecha y
 *                        contadores de tarjetas vía GET /views?videos=h1,h2,...
 *   <video-forum-stats>  En el detalle: GET stats iniciales + POST /views (beacon,
 *                        1 vez por sesión por video).
 *   <video-like-button>  Toggle de like para video o comentario (POST/DELETE /likes).
 *   <video-comments>     Árbol de comentarios: listar, ordenar, comentar, responder
 *                        (máx. 2 niveles) y likes por comentario.
 *
 * Degradación: si el proxy no responde (app sin desplegar, red, etc.) los
 * contadores permanecen ocultos y la sección de comentarios se oculta —
 * nunca se rompe la página.
 *
 * Seguridad: todo el UGC se pinta con textContent / createElement, nunca innerHTML.
 */

(() => {
  const DEFAULT_BASE = '/apps/video-forum';

  const MESSAGES = {
    login_required: 'Inicia sesión para participar.',
    empty_body: 'Escribe un comentario antes de publicar.',
    body_too_long: 'El comentario supera los 2000 caracteres.',
    rate_limited: 'Estás comentando muy rápido. Espera un momento e intenta de nuevo.',
    parent_not_found: 'El comentario al que respondes ya no existe.',
    invalid_username: 'Ese nombre no se puede usar: 3–24 caracteres, sin @, enlaces ni palabras ofensivas.',
    default: 'No se pudo completar la acción. Intenta de nuevo.',
  };

  function proxyBase(el) {
    return el.getAttribute('data-proxy-base') || DEFAULT_BASE;
  }

  async function vfFetch(base, path, options = {}) {
    const opts = { headers: { Accept: 'application/json' }, ...options };
    if (opts.body && typeof opts.body !== 'string') {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(opts.body);
    }
    const response = await fetch(base + path, opts);
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      /* respuesta sin JSON (proxy caído / HTML de error) */
    }
    return { ok: response.ok, status: response.status, data };
  }

  function errorMessage(result) {
    const code = result && result.data && result.data.error;
    return MESSAGES[code] || MESSAGES.default;
  }

  function formatCount(value) {
    const n = Number(value) || 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.0', '')} M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')} k`;
    return String(n);
  }

  // El proxy nunca debería mandar correos como nombre, pero por privacidad
  // el theme tampoco los pinta si llegaran (comentarios viejos, bugs de la app).
  function publicName(raw) {
    const name = (raw || '').trim();
    return !name || name.includes('@') ? 'Cliente' : name;
  }

  function relativeDate(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'hace un momento';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? 'hace 1 hora' : `hace ${hours} horas`;
    const days = Math.floor(hours / 24);
    if (days < 30) return days === 1 ? 'hace 1 día' : `hace ${days} días`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function fillStat(scope, stat, value) {
    scope.querySelectorAll(`[data-vf-stat="${stat}"]`).forEach((el) => {
      const target = el.querySelector('[data-vf-stat-value]') || el;
      target.textContent = formatCount(value);
      el.hidden = false;
    });
  }

  /* ------------------------------------------------------------------ */
  /* <video-forum-feed>                                                  */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('video-forum-feed')) {
    class VideoForumFeed extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-vf-grid]');
        this.emptyMessage = this.querySelector('[data-vf-empty-filter]');
        if (!this.grid) return;

        this.sortCards();
        this.bindFilters();
        this.bindSearch();
        this.loadStats();
      }

      cards() {
        return Array.from(this.grid.querySelectorAll('[data-vf-item]'));
      }

      sortCards() {
        // Una transmisión manda sobre la fecha: primero lo que está al aire,
        // después lo programado, y el resto por fecha descendente. El estado lo
        // decide Liquid (video-forum-live-state) y llega en data-live.
        const rank = (card) => {
          if (card.dataset.live === 'live') return 0;
          if (card.dataset.live === 'upcoming') return 1;
          return 2;
        };

        this.cards()
          .sort(
            (a, b) =>
              rank(a) - rank(b) || (b.dataset.date || '').localeCompare(a.dataset.date || '')
          )
          .forEach((card) => this.grid.appendChild(card));
      }

      bindFilters() {
        this.filterButtons = Array.from(this.querySelectorAll('[data-vf-category]'));
        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.activeCategory = button.dataset.vfCategory;
            this.filterButtons.forEach((b) => {
              const active = b === button;
              b.classList.toggle('vf-feed__category--active', active);
              b.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
            this.applyFilters();
          });
        });
        this.activeCategory = 'all';
      }

      bindSearch() {
        const input = this.querySelector('[data-vf-search]');
        if (!input) return;
        input.addEventListener('input', () => {
          this.searchTerm = input.value.trim().toLowerCase();
          this.applyFilters();
        });
      }

      applyFilters() {
        let visible = 0;
        this.cards().forEach((card) => {
          const matchesCategory =
            this.activeCategory === 'all' || card.dataset.category === this.activeCategory;
          const haystack = `${card.dataset.title || ''} ${card.dataset.author || ''}`.toLowerCase();
          const matchesSearch = !this.searchTerm || haystack.includes(this.searchTerm);
          const show = matchesCategory && matchesSearch;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (this.emptyMessage) this.emptyMessage.hidden = visible > 0;
      }

      async loadStats() {
        const handles = this.cards()
          .map((card) => card.dataset.handle)
          .filter(Boolean);
        if (!handles.length) return;

        const base = proxyBase(this);
        // El endpoint acepta máx. 50 handles por request
        for (let i = 0; i < handles.length; i += 50) {
          const chunk = handles.slice(i, i + 50);
          try {
            const result = await vfFetch(base, `/views?videos=${encodeURIComponent(chunk.join(','))}`);
            if (!result.ok || !result.data || !result.data.stats) continue;
            chunk.forEach((handle) => {
              const stats = result.data.stats[handle];
              const card = this.grid.querySelector(`[data-vf-item][data-handle="${CSS.escape(handle)}"]`);
              if (!stats || !card) return;
              fillStat(card, 'views', stats.views);
              fillStat(card, 'likes', stats.likes);
              fillStat(card, 'comments', stats.comments);
            });
          } catch (_) {
            /* proxy no disponible: contadores permanecen ocultos */
          }
        }
      }
    }
    customElements.define('video-forum-feed', VideoForumFeed);
  }

  /* ------------------------------------------------------------------ */
  /* <video-forum-stats> — detalle: stats iniciales + beacon de vista    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('video-forum-stats')) {
    class VideoForumStats extends HTMLElement {
      connectedCallback() {
        this.video = this.getAttribute('data-video');
        if (!this.video) return;
        this.scope = this.closest('[data-vf-detail]') || document;
        this.load();
      }

      async load() {
        const base = proxyBase(this);
        try {
          const result = await vfFetch(base, `/views?videos=${encodeURIComponent(this.video)}`);
          const stats = result.ok && result.data && result.data.stats && result.data.stats[this.video];
          if (stats) {
            fillStat(this.scope, 'views', stats.views);
            fillStat(this.scope, 'comments', stats.comments);
            const likeButton = this.scope.querySelector('video-like-button[data-target-type="video"]');
            if (likeButton) likeButton.setCount(stats.likes);
          }
        } catch (_) {
          /* sin stats */
        }
        this.beacon(base);
      }

      async beacon(base) {
        const key = `vf-viewed-${this.video}`;
        try {
          if (sessionStorage.getItem(key)) return;
        } catch (_) {
          /* sessionStorage bloqueado: registrar la vista de todas formas */
        }
        try {
          const result = await vfFetch(base, '/views', { method: 'POST', body: { video: this.video } });
          if (result.ok && result.data && typeof result.data.views !== 'undefined') {
            fillStat(this.scope, 'views', result.data.views);
            try {
              sessionStorage.setItem(key, '1');
            } catch (_) {
              /* noop */
            }
          }
        } catch (_) {
          /* sin beacon */
        }
      }
    }
    customElements.define('video-forum-stats', VideoForumStats);
  }

  /* ------------------------------------------------------------------ */
  /* <video-like-button>                                                 */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('video-like-button')) {
    class VideoLikeButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button') || this.buildButton();
        this.countEl = this.querySelector('[data-vf-like-count]');
        this.liked = this.getAttribute('data-liked') === 'true';
        this.pending = false;
        this.render();
        this.button.addEventListener('click', () => this.toggle());
      }

      buildButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vf-like';
        const count = document.createElement('span');
        count.setAttribute('data-vf-like-count', '');
        button.append('♥ ', count);
        this.appendChild(button);
        this.countEl = count;
        return button;
      }

      setCount(value) {
        this.count = Number(value) || 0;
        if (this.countEl) {
          this.countEl.textContent = formatCount(this.count);
          this.countEl.hidden = false;
        }
      }

      setLiked(liked) {
        this.liked = Boolean(liked);
        this.render();
      }

      render() {
        this.button.classList.toggle('vf-like--active', this.liked);
        this.button.setAttribute('aria-pressed', this.liked ? 'true' : 'false');
      }

      async toggle() {
        if (this.pending) return;

        if (this.getAttribute('data-logged-in') === null) {
          const loginUrl = this.getAttribute('data-login-url');
          if (loginUrl) window.location.href = loginUrl;
          return;
        }

        this.pending = true;
        this.button.disabled = true;
        try {
          const result = await vfFetch(proxyBase(this), '/likes', {
            method: this.liked ? 'DELETE' : 'POST',
            body: {
              targetType: this.getAttribute('data-target-type') || 'video',
              targetId: this.getAttribute('data-target-id'),
            },
          });
          if (result.ok && result.data) {
            this.setLiked(result.data.liked);
            this.setCount(result.data.likes);
          } else if (result.status === 401) {
            const loginUrl = this.getAttribute('data-login-url');
            if (loginUrl) window.location.href = loginUrl;
          }
        } catch (_) {
          /* proxy no disponible: no cambiar estado */
        } finally {
          this.pending = false;
          this.button.disabled = false;
        }
      }
    }
    customElements.define('video-like-button', VideoLikeButton);
  }

  /* ------------------------------------------------------------------ */
  /* <video-comments>                                                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('video-comments')) {
    class VideoComments extends HTMLElement {
      connectedCallback() {
        this.video = this.getAttribute('data-video');
        if (!this.video) return;
        this.loggedIn = this.hasAttribute('data-logged-in');
        this.loginUrl = this.getAttribute('data-login-url') || '/account/login';
        this.maxLength = Number(this.getAttribute('data-max-length')) || 2000;
        this.sort = 'recent';

        this.listEl = this.querySelector('[data-vf-comments-list]');
        this.countEl = this.querySelector('[data-vf-comments-count]');
        this.sortEl = this.querySelector('[data-vf-comments-sort]');
        this.formEl = this.querySelector('[data-vf-comment-form]');
        this.feedbackEl = this.querySelector('[data-vf-comments-feedback]');

        if (this.sortEl) {
          this.sortEl.addEventListener('change', () => {
            this.sort = this.sortEl.value;
            this.load();
          });
        }
        if (this.formEl) this.bindForm(this.formEl, null);
        this.load();
        this.initProfile();
      }

      /* --- Username del foro (metafield del cliente vía /profile) --- */

      async initProfile() {
        if (!this.loggedIn || !this.formEl) return;
        try {
          const result = await vfFetch(proxyBase(this), '/profile');
          if (!result.ok || !result.data || result.data.error) return;
          this.profile = result.data;
          this.renderProfileBar();
        } catch (_) {
          /* app sin /profile o proxy caído: se comenta con el nombre de la cuenta */
        }
      }

      renderProfileBar() {
        if (!this.profileBar) {
          this.profileBar = document.createElement('div');
          this.profileBar.className = 'vf-profile';
          this.formEl.before(this.profileBar);
        }
        this.profileBar.textContent = '';

        const label = document.createElement('span');
        label.className = 'vf-profile__label';
        label.append('Comentando como ');
        const strong = document.createElement('strong');
        strong.textContent = publicName(this.profile.username || this.profile.fallbackName);
        label.appendChild(strong);

        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'vf-profile__edit';
        edit.textContent = this.profile.username ? 'Cambiar' : 'Elegir mi nombre para el foro';
        edit.addEventListener('click', () => this.toggleProfileForm());

        this.profileBar.append(label, edit);
      }

      toggleProfileForm() {
        if (this.profileForm) {
          this.profileForm.remove();
          this.profileForm = null;
          return;
        }
        const form = document.createElement('form');
        form.className = 'vf-profile__form';

        const input = document.createElement('input');
        input.type = 'text';
        input.required = true;
        input.minLength = 3;
        input.maxLength = 24;
        input.placeholder = 'Tu nombre para el foro';
        input.value = this.profile.username || '';
        input.setAttribute('aria-label', 'Nombre de usuario para el foro');

        const save = document.createElement('button');
        save.type = 'submit';
        save.className = 'vf-btn vf-btn--primary';
        save.textContent = 'Guardar';

        const hint = document.createElement('p');
        hint.className = 'vf-profile__hint';
        hint.textContent = 'Se mostrará en tus próximos comentarios (3–24 caracteres).';

        const feedback = document.createElement('p');
        feedback.className = 'vf-comments__feedback';
        feedback.setAttribute('data-vf-form-feedback', '');
        feedback.hidden = true;

        form.append(input, save, hint, feedback);
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          save.disabled = true;
          try {
            const result = await vfFetch(proxyBase(this), '/profile', {
              method: 'POST',
              body: { username: input.value },
            });
            if (result.ok && result.data && result.data.username) {
              this.profile.username = result.data.username;
              form.remove();
              this.profileForm = null;
              this.renderProfileBar();
            } else if (result.status === 401) {
              window.location.href = this.loginUrl;
            } else {
              feedback.textContent = errorMessage(result);
              feedback.hidden = false;
            }
          } catch (_) {
            feedback.textContent = MESSAGES.default;
            feedback.hidden = false;
          } finally {
            save.disabled = false;
          }
        });

        this.profileBar.after(form);
        this.profileForm = form;
        input.focus();
      }

      async load() {
        const result = await vfFetch(
          proxyBase(this),
          `/comments?video=${encodeURIComponent(this.video)}&sort=${encodeURIComponent(this.sort)}`
        ).catch(() => null);

        if (!result || !result.ok || !result.data || !Array.isArray(result.data.comments)) {
          // Proxy no disponible: ocultar la sección completa, no romper la página
          this.hidden = true;
          return;
        }
        this.hidden = false;
        this.renderList(result.data.comments);
      }

      renderList(comments) {
        if (!this.listEl) return;
        this.listEl.textContent = '';
        let total = 0;
        comments.forEach((comment) => {
          total += 1 + (comment.replies ? comment.replies.length : 0);
          this.listEl.appendChild(this.renderComment(comment, null));
        });
        if (this.countEl) this.countEl.textContent = formatCount(total);
        fillStat(this.closest('[data-vf-detail]') || this, 'comments', total);

        const empty = this.querySelector('[data-vf-comments-empty]');
        if (empty) empty.hidden = comments.length > 0;
      }

      renderComment(comment, rootId) {
        const item = document.createElement('article');
        item.className = rootId ? 'vf-comment vf-comment--reply' : 'vf-comment';

        const displayName = publicName(comment.customerName);

        const avatar = document.createElement('span');
        avatar.className = 'vf-comment__avatar';
        avatar.textContent = displayName.slice(0, 2).toUpperCase();

        const body = document.createElement('div');
        body.className = 'vf-comment__body';

        const header = document.createElement('header');
        header.className = 'vf-comment__header';
        const name = document.createElement('strong');
        name.textContent = displayName;
        const time = document.createElement('time');
        time.textContent = relativeDate(comment.createdAt);
        header.append(name, time);
        if (comment.status === 'pending') {
          const badge = document.createElement('span');
          badge.className = 'vf-comment__pending';
          badge.textContent = 'En revisión';
          header.appendChild(badge);
        }

        const text = document.createElement('p');
        text.className = 'vf-comment__text';
        text.textContent = comment.body || '';

        const actions = document.createElement('div');
        actions.className = 'vf-comment__actions';

        if (comment.id && comment.status !== 'pending') {
          const like = document.createElement('video-like-button');
          like.className = 'vf-comment__like';
          like.setAttribute('data-target-type', 'comment');
          like.setAttribute('data-target-id', comment.id);
          like.setAttribute('data-proxy-base', proxyBase(this));
          like.setAttribute('data-login-url', this.loginUrl);
          if (this.loggedIn) like.setAttribute('data-logged-in', '');
          if (comment.likedByMe) like.setAttribute('data-liked', 'true');
          actions.appendChild(like);
          requestAnimationFrame(() => like.setCount(comment.likes));

          if (this.loggedIn) {
            const reply = document.createElement('button');
            reply.type = 'button';
            reply.className = 'vf-comment__reply-btn';
            reply.textContent = 'Responder';
            // Máx. 2 niveles: responder a una respuesta cuelga del comentario raíz
            const parentId = rootId || comment.id;
            reply.addEventListener('click', () =>
              this.toggleReplyForm(item, parentId, rootId ? displayName : null)
            );
            actions.appendChild(reply);
          }
        }

        body.append(header, text, actions);
        item.append(avatar, body);

        if (!rootId && Array.isArray(comment.replies) && comment.replies.length) {
          const replies = document.createElement('div');
          replies.className = 'vf-comment__replies';
          comment.replies.forEach((reply) => replies.appendChild(this.renderComment(reply, comment.id)));
          body.appendChild(replies);
        }

        return item;
      }

      toggleReplyForm(item, parentId, mentionName) {
        const existing = item.querySelector('form[data-vf-reply-form]');
        if (existing) {
          existing.remove();
          return;
        }
        const form = document.createElement('form');
        form.setAttribute('data-vf-reply-form', '');
        form.className = 'vf-comment__reply-form';

        const textarea = document.createElement('textarea');
        textarea.required = true;
        textarea.maxLength = this.maxLength;
        textarea.rows = 2;
        textarea.placeholder = 'Escribe tu respuesta…';
        if (mentionName) textarea.value = `@${mentionName} `;

        const submit = document.createElement('button');
        submit.type = 'submit';
        submit.className = 'vf-btn vf-btn--primary';
        submit.textContent = 'Responder';

        const feedback = document.createElement('p');
        feedback.className = 'vf-comments__feedback';
        feedback.setAttribute('data-vf-form-feedback', '');
        feedback.hidden = true;

        form.append(textarea, submit, feedback);
        const bodyEl = item.querySelector('.vf-comment__body');
        (bodyEl || item).appendChild(form);
        this.bindForm(form, parentId);
        textarea.focus();
      }

      bindForm(form, parentId) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const textarea = form.querySelector('textarea');
          const submit = form.querySelector('[type="submit"]');
          const body = (textarea.value || '').trim();
          if (!body) {
            this.showFeedback(form, MESSAGES.empty_body);
            return;
          }
          submit.disabled = true;
          try {
            const payload = { video: this.video, body };
            if (parentId) payload.parentId = parentId;
            const result = await vfFetch(proxyBase(this), '/comments', { method: 'POST', body: payload });
            if (result.status === 201 && result.data && result.data.comment) {
              textarea.value = '';
              this.showFeedback(form, 'Tu comentario se publicará cuando sea aprobado.', true);
              this.prependPending(result.data.comment, parentId, form);
            } else if (result.status === 401) {
              window.location.href = this.loginUrl;
            } else {
              this.showFeedback(form, errorMessage(result));
            }
          } catch (_) {
            this.showFeedback(form, MESSAGES.default);
          } finally {
            submit.disabled = false;
          }
        });
      }

      prependPending(comment, parentId, form) {
        if (!this.listEl) return;
        const rendered = this.renderComment({ ...comment, status: 'pending' }, parentId);
        if (parentId) {
          rendered.classList.add('vf-comment--reply');
          const bodyEl = form.closest('.vf-comment__body');
          if (bodyEl) {
            let replies = bodyEl.querySelector('.vf-comment__replies');
            if (!replies) {
              replies = document.createElement('div');
              replies.className = 'vf-comment__replies';
              bodyEl.appendChild(replies);
            }
            replies.appendChild(rendered);
            form.remove();
            return;
          }
        }
        this.listEl.prepend(rendered);
        const empty = this.querySelector('[data-vf-comments-empty]');
        if (empty) empty.hidden = true;
      }

      showFeedback(form, message, success = false) {
        const el = form.querySelector('[data-vf-form-feedback]') || this.feedbackEl;
        if (!el) return;
        el.textContent = message;
        el.classList.toggle('vf-comments__feedback--success', success);
        el.hidden = false;
        clearTimeout(this.feedbackTimer);
        this.feedbackTimer = setTimeout(() => {
          el.hidden = true;
        }, 6000);
      }
    }
    customElements.define('video-comments', VideoComments);
  }

  /* ------------------------------------------------------------------ */
  /* <video-share-button>                                                */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('video-share-button')) {
    class VideoShareButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.button.addEventListener('click', () => this.share());
      }

      async share() {
        const url = this.getAttribute('data-url') || window.location.href;
        const title = this.getAttribute('data-title') || document.title;
        try {
          if (navigator.share) {
            await navigator.share({ title, url });
            return;
          }
          await navigator.clipboard.writeText(url);
          const original = this.button.textContent;
          this.button.textContent = '¡Enlace copiado!';
          setTimeout(() => {
            this.button.textContent = original;
          }, 2000);
        } catch (_) {
          /* usuario canceló el share */
        }
      }
    }
    customElements.define('video-share-button', VideoShareButton);
  }
})();
